import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faBullseye, faPiggyBank } from "@fortawesome/free-solid-svg-icons";

import { CURRENCY_SYMBOL_MAP } from "../utils/constants";
import CurrencyInput from "react-currency-input-field";

function SavingGoalList({ showDeleteButton = true, showEditButton = true, refreshTrigger }) {
  const { t } = useTranslation();
  const [savingGoals, setSavingGoals] = useState([]);

  useEffect(() => {
    fetchSavingGoals();
  }, [refreshTrigger]);

  const fetchSavingGoals = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/saving-goals`);
      setSavingGoals(response.data);
    } catch (error) {
      console.error("Error fetching saving goals:", error);
    }
  };

  const handleUpdate = async (id, newSavedAmount) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/saving-goals/${id}`, { savedAmount: newSavedAmount });
      fetchSavingGoals();
    } catch (error) {
      console.error("Error updating saving goal:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t("areYouSureYouWantToDeleteThisSavingGoal"))) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/saving-goals/${id}`);
        fetchSavingGoals();
      } catch (error) {
        console.error("Error deleting saving goal:", error);
      }
    }
  };

  return (
    <div className="space-y-4">
      <AnimatePresence>
        {savingGoals.map((goal) => {
          const percentage = (goal.savedAmount / goal.goal) * 100;
          return (
            <motion.div
              key={goal._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="card bg-white dark:bg-dark-surface p-2 transition-colors"
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                  <FontAwesomeIcon icon={faBullseye} className="text-primary-500 text-sm" />
                  {goal.name}
                </h3>
                <div>
                  {showDeleteButton && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(goal._id);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1 relative z-20 pointer-events-auto"
                      title={t("delete")}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              </div>

              <div className="mb-4 rounded-xl overflow-hidden aspect-video w-full">
                {goal.imageUrl ? (
                  <img src={goal.imageUrl} alt={goal.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/40 dark:to-primary-800/40 flex items-center justify-center">
                    <FontAwesomeIcon icon={faPiggyBank} className="text-5xl text-primary-400 dark:text-primary-500" />
                  </div>
                )}
              </div>

              <div className="mt-2">
                <div className="flex justify-between items-end text-sm text-gray-600 mb-2">
                  <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg px-2 py-1 border border-gray-100 dark:border-gray-600 transition-colors">
                    <CurrencyInput
                      value={goal.savedAmount}
                      onValueChange={(value) => {
                        const newValue = value || 0;
                        if (newValue !== goal.savedAmount) {
                          handleUpdate(goal._id, newValue);
                        }
                      }}
                      className="w-24 !bg-transparent border-none outline-none focus:outline-none focus:ring-0 font-bold text-primary-700 dark:text-primary-300"
                      decimalsLimit={0}
                      suffix={` ${CURRENCY_SYMBOL_MAP[goal.currency]}`}
                    />
                  </div>
                  <span className="font-medium text-gray-500 dark:text-gray-400">
                    {t("of")}{" "}
                    <span className="text-gray-800 dark:text-gray-200 font-bold">
                      {goal.goal} {CURRENCY_SYMBOL_MAP[goal.currency]}
                    </span>
                  </span>
                </div>

                <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-3 overflow-hidden transition-colors">
                  <motion.div
                    className={`h-full rounded-full ${
                      percentage < 33 ? "bg-red-500" : percentage < 66 ? "bg-orange-500" : "bg-green-500"
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage > 100 ? 100 : percentage}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                </div>
                <div className="text-right mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">{Math.round(percentage)}%</div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

export default SavingGoalList;
